import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Upload, Loader2, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { api, assetUrl } from '../lib/api';

export default function MediaLibrary() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/admin/media').then((res) => setFiles(res.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post('/admin/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('File uploaded');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('URL copied – paste it in any image field');
  };

  const isImage = (name) => /\.(jpg|jpeg|png|webp|gif|svg)$/i.test(name);

  return (
    <div data-testid="admin-media-library">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-[#26247B]">Media Library</h1>
          <p className="mt-1 text-sm text-black/60">All uploaded images, videos and files. Click a file to copy its URL.</p>
        </div>
        <button
          onClick={() => inputRef.current?.click()}
          data-testid="admin-media-library-upload-button"
          disabled={uploading}
          className="inline-flex items-center gap-2 rounded-lg bg-[#26247B] text-white px-5 py-2.5 text-sm font-bold transition-colors duration-200 hover:bg-[#1f1d66] disabled:opacity-60"
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload File
        </button>
        <input ref={inputRef} type="file" accept="image/*,video/mp4,video/webm,.pdf" className="hidden" onChange={handleFile} />
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#26247B]" /></div>
      ) : (
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
          {files.map((f) => (
            <button
              key={f.filename}
              onClick={() => copyUrl(f.url)}
              className="group bg-white border border-[#E6E8EE] rounded-xl overflow-hidden text-left hover:border-[#26247B] transition-colors duration-200"
              title="Click to copy URL"
            >
              {isImage(f.filename) ? (
                <img src={assetUrl(f.url)} alt={f.filename} className="w-full h-28 object-cover" loading="lazy" />
              ) : (
                <div className="w-full h-28 bg-[#F6F7F9] flex items-center justify-center text-xs font-bold text-black/50 uppercase">{f.filename.split('.').pop()}</div>
              )}
              <div className="px-3 py-2 flex items-center justify-between gap-1">
                <span className="text-[11px] text-black/70 truncate">{f.filename}</span>
                <Copy className="h-3.5 w-3.5 text-black/40 group-hover:text-[#26247B] shrink-0" />
              </div>
            </button>
          ))}
          {files.length === 0 && <p className="col-span-full text-center text-sm text-black/60 py-12">No files uploaded yet.</p>}
        </div>
      )}
    </div>
  );
}
